import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Search } from 'lucide-react';
import { PRODUCTS, CATEGORIES } from '../mock/mock';
import ProductCard from '../components/ProductCard';

const SORTS = [
  { id: 'featured', label: 'Featured' },
  { id: 'price-low', label: 'Price: Low to High' },
  { id: 'price-high', label: 'Price: High to Low' },
  { id: 'rating', label: 'Top Rated' },
];

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get('cat') || 'all';
  const [sort, setSort] = useState('featured');
  const [query, setQuery] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const setCat = (id) => {
    if (id === 'all') setParams({});
    else setParams({ cat: id });
  };

  const filtered = useMemo(() => {
    let list = [...PRODUCTS];
    if (activeCat !== 'all') list = list.filter((p) => p.category === activeCat);
    if (query.trim()) list = list.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));
    if (sort === 'price-low') list.sort((a, b) => a.price - b.price);
    if (sort === 'price-high') list.sort((a, b) => b.price - a.price);
    if (sort === 'rating') list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [activeCat, sort, query]);

  const catName = activeCat === 'all' ? 'All Products' : CATEGORIES.find((c) => c.id === activeCat)?.name;

  return (
    <div>
      {/* Banner */}
      <div className="bg-[#141414] text-white py-14">
        <div className="container-fes">
          <span className="section-label">Shop</span>
          <h1 className="font-head text-4xl md:text-5xl uppercase mt-2">{catName}</h1>
          <p className="text-gray-300 mt-2">{filtered.length} products · Field-proven medical equipment</p>
        </div>
      </div>

      <div className="container-fes py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-28 h-max">
          <div className="relative mb-6">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-[#c8102e] transition-colors text-sm"
            />
          </div>
          <h3 className="font-head uppercase text-[#141414] mb-3 flex items-center gap-2"><SlidersHorizontal size={16} /> Categories</h3>
          <ul className="space-y-1">
            <li>
              <button onClick={() => setCat('all')} className={`w-full text-left px-3 py-2 rounded font-cond transition-colors ${activeCat === 'all' ? 'bg-[#15a34a] text-white' : 'text-[#141414] hover:bg-[#f4f6fa]'}`}>All Products</button>
            </li>
            {CATEGORIES.map((c) => (
              <li key={c.id}>
                <button onClick={() => setCat(c.id)} className={`w-full text-left px-3 py-2 rounded font-cond transition-colors ${activeCat === c.id ? 'bg-[#15a34a] text-white' : 'text-[#141414] hover:bg-[#f4f6fa]'}`}>{c.name}</button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Grid */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-gray-500 font-cond uppercase tracking-wide">Showing {filtered.length} items</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-cond focus:outline-none focus:border-[#c8102e] cursor-pointer">
              {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400 font-cond text-lg">No products found.</div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
